import React, { useState, forwardRef, useMemo, useEffect } from 'react';
import { NoteFilter, NoteOrderBy, NoteOrder } from '../types/note';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parse } from 'date-fns';
import './SearchBar.module.css';
import {ALL_NOTES, ROOT_LEVEL} from "../utils/constants";

interface SearchBarProps {
  onFilterChange: (filter: NoteFilter) => void;
  selectedCategoryId: number;
  categoryBreadcrumb: string;
  initialFilter: NoteFilter;
}

const CustomInput = forwardRef<HTMLInputElement, any>(({ value, onClick, onChange, placeholder }, ref) => (
  <div className="w-full custom-datepicker relative">
    <input
      ref={ref}
      value={value}
      onClick={onClick}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 pr-10 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-teal focus:border-transparent min-h-[48px] placeholder-gray-400"
      style={{ minHeight: 48, height: 48, width: '100%' }}
      autoComplete="off"
    />
    <button
      type="button"
      tabIndex={-1}
      className="absolute right-7 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-brand-teal focus:outline-none"
      onClick={onClick}
      style={{ background: 'none', border: 'none', padding: 0, margin: 0 }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="18" rx="2" fill="#fff" stroke="#d1d5db" strokeWidth="2" />
        <path d="M8 2v4M16 2v4M3 10h18" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </button>
  </div>
));

const parseDateString = (str: string) => {
  // Accept dd/mm/yyyy or d-m-yyyy
  const match = str.match(/^\s*(\d{1,2})[/-](\d{1,2})[/-](\d{4})\s*$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const parsed = parse(`${day}/${month}/${year}`, 'dd/MM/yyyy', new Date());
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateInput = (input: string) => {
  // Remove all non-digits
  let digits = input.replace(/\D/g, '');
  if (digits.length > 8) digits = digits.slice(0, 8);
  let formatted = '';
  if (digits.length > 0) formatted += digits.slice(0, 2);
  if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
  if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
  return formatted;
};

const SearchBar: React.FC<SearchBarProps> = ({ onFilterChange, selectedCategoryId, categoryBreadcrumb, initialFilter }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Initialize state without triggering filter updates
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [orderBy, setOrderBy] = useState<NoteOrderBy>(NoteOrderBy.created_at);
  const [order, setOrder] = useState<NoteOrder>(NoteOrder.desc);

  // Set initial values from props without triggering filter
  useEffect(() => {
    // Only initialize values when the component mounts or when category changes
    if (!initialFilter.isApplied) {
      if (selectedCategoryId === initialFilter.category_id) {
        setSearch(initialFilter.search || '');
        setStartDate(initialFilter.start_date ? new Date(initialFilter.start_date) : null);
        setEndDate(initialFilter.end_date ? new Date(initialFilter.end_date) : null);
        setOrderBy(initialFilter.order_by || NoteOrderBy.created_at);
        setOrder(initialFilter.order || NoteOrder.desc);
      } else {
        // Category changed - reset values
        console.log('[DEBUG] SearchBar: Category changed, resetting values');
        setSearch('');
        setStartDate(null);
        setEndDate(null);
        setOrderBy(NoteOrderBy.created_at);
        setOrder(NoteOrder.desc);
      }
    }
  }, [selectedCategoryId, initialFilter]);

  const handleApplyFilter = () => {
    console.log('[DEBUG] SearchBar: Apply filter button clicked');
    const filter = {
      search,
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      order_by: orderBy,
      order,
      category_id: selectedCategoryId,
      skip: initialFilter.skip,
      limit: initialFilter.limit,
      isApplied: true // Flag to indicate this came from Apply button
    };
    console.log('[DEBUG] SearchBar: Applying filter', { filter });
    onFilterChange(filter);
  };

  // Sync input string with date state
  React.useEffect(() => {
    setStartDateInput(startDate ? format(startDate, 'dd/MM/yyyy') : '');
  }, [startDate]);
  React.useEffect(() => {
    setEndDateInput(endDate ? format(endDate, 'dd/MM/yyyy') : '');
  }, [endDate]);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };
  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Helper to set input value and move caret after auto-inserted '/'
  const setInputWithCaret = (inputEl: HTMLInputElement, formatted: string, prevValue: string, prevCaret: number) => {
    inputEl.value = formatted;
    let caret = prevCaret;
    // If user is typing at the end, always move caret to end
    if (caret >= prevValue.length) {
      caret = formatted.length;
    } else if (formatted.length > prevValue.length) {
      // If a '/' was just inserted at the caret position, move caret past it
      if ((caret === 2 || caret === 5) && formatted[caret - 1] !== '/' && formatted[caret] === '/') {
        caret++;
      }
    }
    inputEl.setSelectionRange(caret, caret);
  };

  const handleStartDateRawChange = (e?: any) => {
    if (e && e.target && e.target instanceof HTMLInputElement) {
      const inputEl = e.target;
      const prevValue = startDateInput;
      const prevCaret = inputEl.selectionStart || 0;
      const input = inputEl.value;
      const formatted = formatDateInput(input);
      setStartDateInput(formatted);
      setInputWithCaret(inputEl, formatted, prevValue, prevCaret);
      const parsed = parseDateString(formatted);
      setStartDate(parsed);
    }
  };
  const handleEndDateRawChange = (e?: any) => {
    if (e && e.target && e.target instanceof HTMLInputElement) {
      const inputEl = e.target;
      const prevValue = endDateInput;
      const prevCaret = inputEl.selectionStart || 0;
      const input = inputEl.value;
      const formatted = formatDateInput(input);
      setEndDateInput(formatted);
      setInputWithCaret(inputEl, formatted, prevValue, prevCaret);
      const parsed = parseDateString(formatted);
      setEndDate(parsed);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({
      ...initialFilter, // Preserve pagination and other initial settings
      search,
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      order_by: orderBy,
      order,
      category_id: selectedCategoryId
    });
  };

  const handleReset = () => {
    setSearch('');
    setStartDate(null);
    setEndDate(null);
    setOrderBy(NoteOrderBy.created_at);
    setOrder(NoteOrder.desc);
    onFilterChange({
      ...initialFilter, // Preserve pagination and other initial settings
      search: '',
      start_date: undefined,
      end_date: undefined,
      order_by: NoteOrderBy.created_at,
      order: NoteOrder.desc,
      category_id: selectedCategoryId // Keep the category filter when resetting
    });
  };

  // Update filter when category changes


  return (
    <div className="mb-6 bg-white dark:bg-[#1a1a1a] rounded-2xl border border-[#048ba8]/10 overflow-hidden transition-all duration-300 ease-in-out">
      {/* Toggle button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-[#0b1d51] dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="font-medium">Search & Filter</span>
        </div>
        <div className="text-sm text-[#849469] truncate">
          {[
            // Category breadcrumb or 'All notes'
            `Searching in ${selectedCategoryId !== ALL_NOTES.id && selectedCategoryId !== ROOT_LEVEL.id ?
            'category: ' + categoryBreadcrumb :
              selectedCategoryId === ROOT_LEVEL.id ? 'unassigned' : 'all notes'
            }`,
            // Other filters
            search && `Search: ${search}`,
            startDate && `From: ${format(startDate, 'MM/dd/yyyy')}`,
            endDate && `To: ${format(endDate, 'MM/dd/yyyy')}`
          ].filter(Boolean).join(' • ')}
        </div>
      </button>

      {/* Collapsible content */}
      <div className={`space-y-4 p-4 ${isExpanded ? 'block' : 'hidden'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Search
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyFilter();
                  }
                }}
                className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-teal focus:border-transparent min-h-[48px]"
              />
            </div>
          </div>

          <div className="space-y-2 w-full">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Start Date
            </label>
            <DatePicker
              id="startDate"
              selected={startDate}
              onChange={handleStartDateChange}
              onChangeRaw={handleStartDateRawChange}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              customInput={<CustomInput value={startDateInput} />}
              isClearable
            />
          </div>

          <div className="space-y-2 w-full">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              End Date
            </label>
            <DatePicker
              id="endDate"
              selected={endDate}
              onChange={handleEndDateChange}
              onChangeRaw={handleEndDateRawChange}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              customInput={<CustomInput value={endDateInput} />}
              isClearable
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="orderBy" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Sort By
            </label>
            <div className="flex items-center gap-4">
              <select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value as NoteOrderBy)}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-teal focus:border-transparent min-h-[48px]"
              >
                <option value={NoteOrderBy.created_at}>Created At</option>
                <option value={NoteOrderBy.updated_at}>Updated At</option>
              </select>
              <button
                type="button"
                onClick={() => setOrder(order === NoteOrder.desc ? NoteOrder.asc : NoteOrder.desc)}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-brand-teal focus:border-transparent min-h-[48px] flex items-center gap-2 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 transform transition-transform ${order === NoteOrder.asc ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="px-6 py-2 text-[#849469] hover:text-[#849469]/90 rounded-2xl border border-[#849469] transition-colors font-medium"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApplyFilter}
            className="px-6 py-2 text-white bg-[#048ba8] hover:bg-[#048ba8]/90 rounded-2xl transition-colors font-medium flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchBar; 